"use client";

import Link from "next/link";
import { useNavigationScroll } from "@/hooks/use-navigation-scroll";
import { useEffect, useRef, useState } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { usePathname, useRouter } from "next/navigation";
import { Shuffle, Menu } from "lucide-react";
import { DocsContentsIcon } from "@/components/icons/docs-contents";
import { DocsProductLogo } from "./product-logo";
import { useDocsScrollRestoration } from "./use-scroll-restoration";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem } from "@/components/ui/select";
import { DocsAccountControls } from "@/providers/account-session";
import { DocsAccountPrompt } from "./account-prompt";
import { DocsGuestRail } from "./guest-rail";
import { DocsContentsContext, type PageContents } from "./page-contents";
import { ContentsNavigation, type DocsHeading } from "./contents-navigation";
import { DocsNavigation } from "./navigation";
import { DocsSearch, DocsSearchTrigger } from "./search";
import { docsContexts, type DocsSearchEntry } from "@/lib/docs/catalog";

function useContextTransition(context: string) {
  const previous = useRef(context);
  const [hasSwitched, setHasSwitched] = useState(false);
  useEffect(() => {
    if (previous.current !== context) {
      previous.current = context;
      setHasSwitched(true);
    }
  }, [context]);
  return hasSwitched ? "docs-context-enter" : "";
}

function DocsSidebar({ context }: { context: string }) {
  const navigationScroll = useNavigationScroll(
    `docs:${context}`,
    usePathname(),
  );
  const contextTransition = useContextTransition(context);
  const { isMobile } = useSidebar();
  return (
    <Sidebar
      collapsible="icon"
      variant="inset"
      onMobileOpenAutoFocus={(event) => {
        event.preventDefault();
        // Focus the panel itself, not the first account tooltip trigger.
        (event.target as HTMLElement).focus({ preventScroll: true });
      }}
      className="overflow-hidden [&>[data-sidebar=sidebar]]:flex-row"
    >
      <DocsAccountControls>
        <DocsGuestRail />
      </DocsAccountControls>
      <Sidebar
        collapsible="none"
        className={`max-h-svh min-h-0 ${isMobile ? "!w-[calc(100%-4.5rem)] flex-1" : "flex-1"}`}
      >
        <SidebarHeader className="gap-4 p-4">
          <DocsProductSwitcher context={context}>
            <Link
              href={`/docs/${context}`}
              className="flex min-w-0 items-center gap-1.5 whitespace-nowrap"
            >
              {docsContexts.find((product) => product.slug === context)?.title}{" "}
              <span className="text-brand-primary dark:text-brand-accent">
                Docs
              </span>
            </Link>
          </DocsProductSwitcher>
          <DocsSearchTrigger />
        </SidebarHeader>
        <SidebarContent
          ref={navigationScroll}
          className="lb-scrollbar overflow-x-hidden"
        >
          <div key={context} className={contextTransition}>
            <DocsNavigation context={context} />
          </div>
        </SidebarContent>
      </Sidebar>
    </Sidebar>
  );
}

function DocsProductSwitcher({
  context,
  children,
}: {
  context: string;
  children?: React.ReactNode;
}) {
  const router = useRouter();
  const { setOpenMobile } = useSidebar();
  return (
    <Select
      value={context}
      onValueChange={(value) => {
        setOpenMobile(false);
        router.push(`/docs/${value}`);
      }}
    >
      <div className="flex w-full items-center justify-between gap-1 text-base font-bold text-foreground">
        {children}
        <SelectPrimitive.Trigger
          aria-label="Switch documentation product"
          className="flex size-7 shrink-0 items-center justify-center rounded-md outline-none transition-colors hover:bg-sidebar-accent hover:text-gray-1100 focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-sidebar-accent dark:hover:text-gray-100"
        >
          <SelectPrimitive.Icon asChild>
            <Shuffle className="size-3.5 shrink-0 translate-y-px" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
      </div>
      <SelectContent align="start" className="motion-reduce:animate-none">
        {docsContexts.map((product) => {
          return (
            <SelectItem key={product.slug} value={product.slug}>
              <span className="flex items-center gap-2">
                <DocsProductLogo product={product.slug} className="size-5" />
                {product.title}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

function DocsMobileHeader({ context }: { context: string }) {
  const product = docsContexts.find((entry) => entry.slug === context)!;
  const { toggleSidebar, openMobile } = useSidebar();
  return (
    <header className="relative z-30 shrink-0 bg-sidebar text-sidebar-foreground md:hidden">
      <div className="flex min-h-14 items-center gap-2 border-b px-3 py-2 sm:px-4">
        <button
          type="button"
          className="flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
          onClick={toggleSidebar}
          aria-expanded={openMobile}
          aria-haspopup="dialog"
        >
          <Menu aria-hidden className="size-[22px]" />
          <span className="sr-only">Toggle menu</span>
        </button>
        <span
          aria-hidden
          className="mr-[7px] h-4 w-px shrink-0 bg-border opacity-80"
        />
        <div className="min-w-0 flex-1">
          <Link
            href={`/docs/${context}`}
            className="block truncate text-sm font-semibold text-foreground"
          >
            {product.title}{" "}
            <span className="text-brand-primary dark:text-brand-accent">
              Docs
            </span>
          </Link>
        </div>
        <div className="flex shrink-0 items-center">
          <DocsSearchTrigger iconOnly />
          <DocsAccountControls placement="header">
            <DocsAccountPrompt kind="user" />
          </DocsAccountControls>
        </div>
      </div>
    </header>
  );
}

export function DocsShell({
  children,
  entries,
  headingsByPath,
}: {
  children: React.ReactNode;
  entries: DocsSearchEntry[];
  headingsByPath: Record<string, DocsHeading[]>;
}) {
  const pathname = usePathname();
  const [pageContents, setPageContents] = useState<PageContents>(null);
  const headings =
    pageContents?.path === pathname
      ? pageContents.headings
      : headingsByPath[pathname];
  const isApiPage =
    pathname === "/docs/nexum/naas-endpoints" ||
    pathname.startsWith("/docs/nexum/symxchange/api-reference");
  useDocsScrollRestoration(pathname);
  const [activeHeading, setActiveHeading] = useState("");
  const [contentsOpen, setContentsOpen] = useState(false);
  const contentsTrigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    setContentsOpen(false);
  }, [pathname]);
  useEffect(() => {
    const container = document.getElementById("docs-scroll-container");
    if (!container || !headings?.length) {
      setActiveHeading("");
      return;
    }
    const update = () => {
      const top = container.getBoundingClientRect().top + 112;
      let current = headings[0].id;
      for (const heading of headings) {
        const element = document.getElementById(heading.id);
        if (element && element.getBoundingClientRect().top <= top) {
          current = heading.id;
        }
      }
      if (
        container.scrollHeight > container.clientHeight &&
        container.scrollTop + container.clientHeight >=
          container.scrollHeight - 2
      ) {
        current = headings[headings.length - 1].id;
      }
      setActiveHeading(current);
    };
    update();
    container.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      container.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [pathname, headings]);
  const context =
    docsContexts.find(
      (product) =>
        pathname === `/docs/${product.slug}` ||
        pathname.startsWith(`/docs/${product.slug}/`),
    )?.slug ?? "poweron-pipelines";
  const contextTransition = useContextTransition(context);
  return (
    <>
      <DocsContentsContext.Provider value={setPageContents}>
        <SidebarProvider className="docs-shell h-svh overflow-hidden">
          <DocsSearch entries={entries}>
            <a
              href="#docs-content"
              className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:bg-background focus:p-3"
            >
              Skip to content
            </a>
            <DocsSidebar context={context} />
            <SidebarInset className="max-h-svh overflow-hidden md:max-h-[calc(100svh-theme(spacing.4))]">
              <div className="flex h-svh min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background md:h-[calc(100svh-theme(spacing.4))] md:rounded-xl">
                <DocsMobileHeader context={context} />
                <div
                  className="lb-scrollbar-overlay min-h-0 flex-1 overflow-x-hidden"
                  id="docs-scroll-container"
                >
                  <div
                    key={context}
                    className={`${contextTransition} mx-auto w-full ${isApiPage ? "max-w-[1040px]" : "max-w-[760px]"} px-5 py-8 sm:px-8 lg:py-10`}
                  >
                    <div
                      className={`docs-reading-content relative mx-auto ${isApiPage ? "max-w-[960px]" : "max-w-[680px]"}`}
                      data-has-sections={true}
                    >
                      <Sheet
                        key={pathname}
                        open={contentsOpen}
                        onOpenChange={setContentsOpen}
                      >
                        <SheetTrigger asChild>
                          <button
                            ref={contentsTrigger}
                            type="button"
                            aria-label="On this page"
                            className="docs-contents-trigger absolute right-0 z-10 flex size-7 items-center justify-center rounded-md border border-input bg-transparent text-muted-foreground transition-colors hover:bg-sidebar-accent focus-visible:outline-brand-primary xl:hidden"
                          >
                            <DocsContentsIcon className="size-4" aria-hidden />
                          </button>
                        </SheetTrigger>
                        <SheetContent
                          side="right"
                          className="docs-controls flex w-72 max-w-[85vw] flex-col gap-0 bg-sidebar p-0 text-sidebar-foreground"
                          onCloseAutoFocus={(event) => {
                            event.preventDefault();
                            contentsTrigger.current?.focus({
                              preventScroll: true,
                            });
                          }}
                        >
                          <SheetTitle className="flex items-center gap-2 px-4 pb-2 pt-5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                            <DocsContentsIcon className="size-4" aria-hidden />
                            On this page
                          </SheetTitle>
                          <SheetDescription className="sr-only">
                            Jump to a section in this documentation page.
                          </SheetDescription>
                          <ContentsNavigation
                            headings={headings ?? []}
                            activeHeading={activeHeading}
                            onSelect={(id) => {
                              setActiveHeading(id);
                              setContentsOpen(false);
                            }}
                          />
                        </SheetContent>
                      </Sheet>
                      {children}
                    </div>
                  </div>
                </div>
              </div>
            </SidebarInset>
            <div className="hidden w-56 shrink-0 xl:block">
              <aside
                aria-label="On this page"
                className="fixed inset-y-0 right-0 flex w-56 flex-col bg-sidebar"
              >
                <div className="flex items-center gap-2 px-4 pb-2 pt-5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  <DocsContentsIcon className="size-4" aria-hidden />
                  ON THIS PAGE
                </div>
                <ContentsNavigation
                  key={pathname}
                  headings={headings ?? []}
                  activeHeading={activeHeading}
                  onSelect={setActiveHeading}
                />
              </aside>
            </div>
          </DocsSearch>
        </SidebarProvider>
      </DocsContentsContext.Provider>
    </>
  );
}
